import React from "react";
import { Link } from "react-router-dom";
import {
  FiCompass,
  FiLayout,
  FiDatabase,
  FiShield,
  FiPackage,
  FiCode,
  FiBarChart2,
  FiHeadphones,
  FiArrowRight,
} from "react-icons/fi";

const services = [
  {
    icon: FiCompass,
    title: "ERP Advisory & Roadmapping",
    description:
      "Understand your processes, define priorities and build a practical Business Central implementation roadmap.",
    cta: "Explore ERP Consulting",
    accent: "blue",
    tint: "from-blue-50 to-white",
    iconBg: "bg-blue-100 text-blue-600",
    check: "bg-blue-100 text-blue-600",
    topBorder: "border-t-blue-500",
    borderHover: "hover:border-slate-200/0",
  },
  {
    icon: FiLayout,
    title: "Business Central Implementation",
    description:
      "Configure finance, sales, purchasing, inventory and operations around the way your organization works.",
    cta: "Explore Implementation",
    featured: true,
    accent: "blue",
    tint: "from-blue-50 to-white",
    iconBg: "bg-blue-100 text-blue-600",
    check: "bg-blue-100 text-blue-600",
    topBorder: "border-t-blue-500",
    borderHover: "hover:border-slate-200/0",
  },
  {
    icon: FiDatabase,
    title: "Tally & Legacy Data Migration",
    description:
      "Move masters, opening balances and transactions through a structured, validated migration process.",
    cta: "Explore Migration",
    accent: "cyan",
    tint: "from-cyan-50 to-white",
    iconBg: "bg-cyan-100 text-cyan-600",
    check: "bg-cyan-100 text-cyan-600",
    topBorder: "border-t-cyan-500",
    borderHover: "hover:border-slate-200/0",
  },
  {
    icon: FiShield,
    title: "Finance, GST & Compliance",
    description:
      "Set up financial processes, GST configurations, reporting structures and statutory workflows for India.",
    cta: "Explore Finance Setup",
    accent: "cyan",
    tint: "from-teal-50 to-white",
    iconBg: "bg-teal-100 text-teal-600",
    check: "bg-teal-100 text-teal-600",
    topBorder: "border-t-teal-500",
    borderHover: "hover:border-slate-200/0",
  },
  {
    icon: FiPackage,
    title: "Inventory & Operations",
    description:
      "Improve visibility across items, warehouses, purchasing, sales and day-to-day operational processes.",
    cta: "Explore Operations",
    accent: "indigo",
    tint: "from-indigo-50 to-white",
    iconBg: "bg-indigo-100 text-indigo-600",
    check: "bg-indigo-100 text-indigo-600",
    topBorder: "border-t-indigo-500",
    borderHover: "hover:border-slate-200/0",
  },
  {
    icon: FiCode,
    title: "Customization & Integration",
    description:
      "Extend Business Central and connect it with existing applications, portals and business workflows.",
    cta: "Explore Integrations",
    accent: "indigo",
    tint: "from-slate-50 to-indigo-50/50",
    iconBg: "bg-indigo-100 text-indigo-600",
    check: "bg-indigo-100 text-indigo-600",
    topBorder: "border-t-indigo-500",
    borderHover: "hover:border-slate-200/0",
  },
  {
    icon: FiBarChart2,
    title: "Reporting & Business Insights",
    description:
      "Turn operational and financial information into clear dashboards, reports and decision-ready insights.",
    cta: "Explore Reporting",
    accent: "violet",
    tint: "from-violet-50 to-white",
    iconBg: "bg-violet-100 text-violet-600",
    check: "bg-violet-100 text-violet-600",
    topBorder: "border-t-violet-500",
    borderHover: "hover:border-slate-200/0",
  },
  {
    icon: FiHeadphones,
    title: "Training & Ongoing Support",
    description:
      "Prepare users for adoption and receive dependable functional and technical assistance after go-live.",
    cta: "Explore Support",
    accent: "violet",
    tint: "from-fuchsia-50 to-white",
    iconBg: "bg-fuchsia-100 text-fuchsia-600",
    check: "bg-fuchsia-100 text-fuchsia-600",
    topBorder: "border-t-fuchsia-500",
    borderHover: "hover:border-slate-200/0",
  },
];

const HowWeHelp = () => {
  return (
    <section className="relative overflow-hidden bg-[#f8f9fc] py-20 md:py-24 lg:py-28">
      {/* Ambient glows */}
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-[28rem] w-[70%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/[0.04] blur-[120px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute right-0 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-violet-500/[0.04] blur-[110px]"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-[1340px] px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div className="about-fade-up mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-bold tracking-[0.12em] text-slate-600 uppercase shadow-sm">
            HOW WE HELP
          </span>

          <h2 className="mt-5 font-raleway font-extrabold leading-[1.12] tracking-tight text-slate-900 text-[clamp(1.75rem,4vw,2.75rem)]">
            End-to-end services for every stage of your{" "}
            <span className="text-[#FFDB16] drop-shadow-[0_0_20px_rgba(255,219,22,0.4)]">
              Business Central journey
            </span>
            .
          </h2>

          <p className="mt-4 font-openSans text-base leading-relaxed text-slate-600 md:text-lg">
            From initial planning and data migration to configuration, go-live
            and ongoing improvement, Abscod provides the practical expertise
            businesses need to implement Business Central with confidence.
          </p>
        </div>

        {/* Cards */}
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((service, index) => (
            <article
              key={service.title}
              style={{ animationDelay: `${(index + 1) * 0.05}s` }}
              className={`about-fade-up group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 border-t-4 ${service.topBorder} bg-gradient-to-br ${service.tint} p-6 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg ${service.borderHover} md:p-7`}
            >
              {/* Icon */}
              <span
                className={`flex h-12 w-12 items-center justify-center rounded-2xl shadow-sm transition-shadow duration-300 group-hover:shadow-[0_0_20px_rgba(37,99,235,0.15)] ${
                  service.featured
                    ? "bg-blue-100 text-blue-600"
                    : service.iconBg
                }`}
              >
                <service.icon
                  className="h-5 w-5"
                  strokeWidth={2}
                  aria-hidden="true"
                />
              </span>

              {/* Content */}
              <h3 className="mt-5 font-raleway text-lg font-bold text-slate-900">
                {service.title}
              </h3>
              <p className="mt-3 flex-1 font-openSans text-sm leading-relaxed text-slate-600">
                {service.description}
              </p>

              {/* CTA */}
              <Link
                to="/contactus"
                className="mt-6 inline-flex items-center gap-2 font-openSans text-sm font-bold text-slate-900 transition-colors duration-200 hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                {service.cta}
                <FiArrowRight
                  className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                  strokeWidth={2.5}
                  aria-hidden="true"
                />
              </Link>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowWeHelp;
